import { Outlet, useLocation, Link, NavLink, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import { MenuIcon, CloseIcon, LogoIcon } from "./Icon";
import { ThemeToggle } from "./ThemeToggle";
import { EmailVerificationBanner } from "./EmailVerificationBanner";
import { useTranslation } from "react-i18next";
import { useConversationList, type ConversationSummary } from "@/hooks/useConversationList";
import { useAuthUser, signOut } from "@/lib/auth";
import { listWorkspaces, createWorkspace, type WsWorkspace } from "@/lib/workspaceApi";
import { STORAGE_UI } from "@/constants/storage";

/* ── Conversation context menu ───────────────────────────────── */

function ConvItem({
  conv,
  onNavigate,
  onDelete,
  onRenameStart,
}: {
  conv: ConversationSummary;
  onNavigate?: () => void;
  onDelete: (id: string) => void;
  onRenameStart: (id: string, currentTitle: string) => void;
}) {
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Close menu on outside click
  useEffect(() => {
    if (!menu) return;
    function onDown(e: MouseEvent | TouchEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenu(null);
      }
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [menu]);

  function openMenu(x: number, y: number) {
    setMenu({ x, y });
  }

  function handleContextMenu(e: React.MouseEvent) {
    e.preventDefault();
    openMenu(e.clientX, e.clientY);
  }

  function handleTouchStart(e: React.TouchEvent) {
    const touch = e.touches[0];
    longPressTimer.current = setTimeout(() => {
      openMenu(touch.clientX, touch.clientY);
    }, 500);
  }

  function handleTouchEnd() {
    if (longPressTimer.current !== null) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }

  function handleTouchMove() {
    if (longPressTimer.current !== null) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }

  return (
    <div className="relative">
      <NavLink
        to={`/chat/${conv.id}`}
        onClick={onNavigate}
        onContextMenu={handleContextMenu}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchMove={handleTouchMove}
        className={({ isActive }) =>
          [
            "block truncate rounded-sm py-[6px] px-2 text-[12px] leading-[1.4] transition select-none",
            isActive
              ? "bg-accent-dim font-medium text-text"
              : "text-text-sec hover:bg-surface-3 hover:text-text",
          ].join(" ")
        }
      >
        {formatConvTitle(conv)}
      </NavLink>

      {menu && (
        <div
          ref={menuRef}
          className="fixed z-[200] min-w-[140px] rounded-md border border-border bg-surface shadow-lg py-1"
          style={{ left: menu.x, top: menu.y }}
        >
          <button
            className="flex w-full items-center gap-2 px-3 py-[7px] text-[12.5px] text-text-sec transition hover:bg-surface-3 hover:text-text"
            onClick={() => {
              setMenu(null);
              onRenameStart(conv.id, formatConvTitle(conv));
            }}
          >
            <PencilIcon />
            Rename
          </button>
          <button
            className="flex w-full items-center gap-2 px-3 py-[7px] text-[12.5px] text-red-500 transition hover:bg-surface-3"
            onClick={() => {
              setMenu(null);
              onDelete(conv.id);
            }}
          >
            <TrashIcon />
            Delete
          </button>
        </div>
      )}
    </div>
  );
}

function RenameInput({
  initialValue,
  onConfirm,
  onCancel,
}: {
  initialValue: string;
  onConfirm: (v: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(initialValue);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  function submit() {
    const trimmed = value.trim();
    if (trimmed) onConfirm(trimmed);
    else onCancel();
  }

  return (
    <input
      ref={inputRef}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") submit();
        if (e.key === "Escape") onCancel();
      }}
      onBlur={submit}
      className="w-full rounded-sm border border-accent bg-surface px-2 py-[5px] text-[12px] text-text outline-none"
    />
  );
}

type NavItem = { to: string; label: string; icon: JSX.Element; badge?: string };

// Rental Tools / Rental Report intentionally removed from the buyer
// sidebar — those surfaces moved to the new /rent app (design v2).
// Market Watch stays because it's a sale-side trend dashboard the
// buyer workspace still uses.
// NavItem.label holds an i18n key here (rendered via t() in SidebarNavSection).
const OTHER_ITEMS: NavItem[] = [
  { to: "/market-watch",  label: "consumerNav.marketWatch",  icon: <WatchIcon /> },
];

// i18n keys — the suggested prompts are display-only links to /chat/new.
const SUGGESTED_PROMPT_KEYS = [
  "consumerNav.suggested1",
  "consumerNav.suggested2",
  "consumerNav.suggested3",
  "consumerNav.suggested4",
  "consumerNav.suggested5",
];

function formatConvTitle(conv: ConversationSummary): string {
  if (conv.title) return conv.title;
  try {
    return new Date(conv.updated_at).toLocaleDateString("en-SG", {
      month: "short",
      day: "numeric",
    });
  } catch {
    return "Chat";
  }
}

function SidebarNavSection({
  label,
  items,
  onNavigate,
}: {
  label: string;
  items: NavItem[];
  onNavigate?: () => void;
}) {
  const { t } = useTranslation();
  return (
    <section className="mb-4">
      <div className="px-1 pb-2 pt-1.5 text-[9.5px] font-semibold uppercase tracking-[0.1em] text-text-dim">
        {label}
      </div>
      <div className="flex flex-col gap-0.5">
        {items.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            onClick={onNavigate}
            className={({ isActive }) =>
              [
                "flex items-center gap-2 rounded-sm border-l-2 py-[7px] pr-2 text-[12.5px] leading-[1.4] transition",
                isActive
                  ? "border-accent bg-accent-dim pl-[6px] font-medium text-text"
                  : "border-transparent pl-2 text-text-sec hover:bg-surface-3 hover:text-text",
              ].join(" ")
            }
          >
            {({ isActive }) => (
              <>
                <span
                  className={`flex h-[13px] w-[13px] shrink-0 items-center justify-center ${
                    isActive ? "text-accent" : "text-text-muted"
                  }`}
                >
                  {it.icon}
                </span>
                <span className="flex-1 truncate">{t(it.label)}</span>
                {it.badge && (
                  <span
                    className="shrink-0 rounded px-1 py-0.5 text-[9px] font-semibold uppercase tracking-[0.08em]"
                    style={{
                      background: "var(--ai-bg)",
                      color: "var(--ai-accent)",
                      border: "1px solid var(--ai-rule)",
                    }}
                  >
                    {it.badge}
                  </span>
                )}
              </>
            )}
          </NavLink>
        ))}
      </div>
    </section>
  );
}

function ConsumerSidebar({ onNavigate }: { onNavigate?: () => void }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const { conversations, loading, refresh, deleteConversation, renameConversation } = useConversationList();

  // renamingId: which conv is showing the inline rename input
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renamingInitial, setRenamingInitial] = useState("");

  const toggleLang = useCallback(() => {
    const next = i18n.language?.startsWith("zh") ? "en" : "zh";
    void i18n.changeLanguage(next);
  }, [i18n]);

  // Workspace section starts open when on /workspace.
  const isOnWorkspace = location.pathname === "/workspace" || location.pathname.startsWith("/workspace/");
  const [workspaceOpen, setWorkspaceOpen] = useState(isOnWorkspace);
  const [workspaces, setWorkspaces] = useState<WsWorkspace[]>([]);
  const [creatingWs, setCreatingWs] = useState(false);
  const [newWsName, setNewWsName] = useState("");
  const [newWsIntent, setNewWsIntent] = useState("");
  const [wsCreateOpen, setWsCreateOpen] = useState(false);

  useEffect(() => {
    if (isOnWorkspace) setWorkspaceOpen(true);
  }, [isOnWorkspace]);

  const refreshWorkspaces = () => listWorkspaces().then(setWorkspaces).catch(() => {});
  useEffect(() => { void refreshWorkspaces(); }, []);

  const handleCreateWorkspace = async () => {
    if (!newWsName.trim() || creatingWs) return;
    setCreatingWs(true);
    try {
      const ws = await createWorkspace(newWsName.trim(), newWsIntent.trim() || "Buy");
      setWorkspaces(prev => [...prev, ws]);
      setWsCreateOpen(false);
      setNewWsName(""); setNewWsIntent("");
      navigate(`/workspace/${ws.id}`);
      if (onNavigate) onNavigate();
    } finally {
      setCreatingWs(false);
    }
  };

  // AI Advisor section starts open when on any /chat route.
  const isOnChat = location.pathname === "/chat" || location.pathname.startsWith("/chat/");
  const [advisorOpen, setAdvisorOpen] = useState(isOnChat);

  useEffect(() => {
    if (isOnChat) setAdvisorOpen(true);
  }, [isOnChat]);

  // Refresh the conversation list whenever the user navigates (a new
  // conversation might have been created, or an existing one updated).
  useEffect(() => {
    void refresh();
  }, [location.pathname, refresh]);

  function handleDelete(id: string) {
    void deleteConversation(id).catch(() => void refresh());
    // If viewing the deleted conversation, redirect to new chat
    if (location.pathname === `/chat/${id}`) {
      navigate("/chat/new");
    }
  }

  function handleRenameStart(id: string, currentTitle: string) {
    setRenamingId(id);
    setRenamingInitial(currentTitle);
  }

  function handleRenameConfirm(id: string, title: string) {
    setRenamingId(null);
    void renameConversation(id, title).catch(() => void refresh());
  }

  return (
    <aside className="flex h-full w-full flex-col border-r border-border bg-surface md:w-[264px] md:min-w-[264px]">
      {/* Logo + controls */}
      <div className="flex shrink-0 items-center justify-between border-b border-border px-4 py-3.5">
        <Link to="/" className="flex items-center gap-2.5">
          <LogoIcon size={26} />
          <div className="leading-none">
            <div
              className="text-[17px] font-semibold tracking-[-0.02em] text-text"
              style={{ fontFamily: '"Source Serif 4", Georgia, serif' }}
            >
              ProProp<span className="text-accent">.</span>
            </div>
            <div className="mt-0.5 text-[9.5px] font-medium uppercase tracking-[0.08em] text-text-muted">
              {t("consumerNav.brandTagline", { defaultValue: "AI Property Advisor" })}
            </div>
          </div>
        </Link>
        <div className="flex items-center gap-1.5">
          <ThemeToggle />
          <button
            onClick={toggleLang}
            title="Switch language"
            className="rounded-md border border-border px-2 py-[3px] text-[11px] font-medium text-text-muted transition hover:border-border-strong hover:text-text"
          >
            {i18n.language?.startsWith("zh") ? "EN" : "中"}
          </button>
        </div>
      </div>

      {/* Scrollable body */}
      <div className="scrollbar-thin flex-1 overflow-y-auto overflow-x-hidden px-3 py-2.5">
        {/* Explore — buyer's default landing */}
        <section className="mb-1">
          <NavLink
            to="/explore"
            onClick={onNavigate}
            className={({ isActive }) =>
              [
                "flex w-full items-center gap-2 rounded-sm border-l-2 py-[7px] pr-2 text-[12.5px] leading-[1.4] transition",
                isActive
                  ? "border-accent bg-accent-dim pl-[6px] font-medium text-text"
                  : "border-transparent pl-2 text-text-sec hover:bg-surface-3 hover:text-text",
              ].join(" ")
            }
          >
            {({ isActive }) => (
              <>
                <span
                  className={`flex h-[13px] w-[13px] shrink-0 items-center justify-center ${
                    isActive ? "text-accent" : "text-text-muted"
                  }`}
                >
                  <ExploreNavIcon />
                </span>
                <span className="flex-1 truncate text-left">{t("consumerNav.explore")}</span>
              </>
            )}
          </NavLink>
        </section>

        {/* Workspace — expandable with project list */}
        <section className="mb-1">
          <button
            onClick={() => setWorkspaceOpen((v) => !v)}
            className={[
              "flex w-full items-center gap-2 rounded-sm border-l-2 py-[7px] pr-2 text-[12.5px] leading-[1.4] transition",
              isOnWorkspace && !workspaceOpen
                ? "border-accent bg-accent-dim pl-[6px] font-medium text-text"
                : "border-transparent pl-2 text-text-sec hover:bg-surface-3 hover:text-text",
            ].join(" ")}
          >
            <span
              className={`flex h-[13px] w-[13px] shrink-0 items-center justify-center ${
                isOnWorkspace ? "text-accent" : "text-text-muted"
              }`}
            >
              <WorkspaceIcon />
            </span>
            <span className="flex-1 truncate text-left">{t("consumerNav.workspace")}</span>
            <span
              className={`flex h-[13px] w-[13px] shrink-0 items-center justify-center text-text-muted transition-transform duration-150 ${
                workspaceOpen ? "rotate-90" : ""
              }`}
            >
              <ChevronIcon />
            </span>
          </button>

          {workspaceOpen && (
            <div className="mt-0.5 ml-[19px] flex flex-col gap-0.5 border-l border-border pl-2 pb-1">
              {/* Create workspace inline form */}
              {wsCreateOpen ? (
                <div className="flex flex-col gap-1.5 py-1">
                  <input
                    autoFocus
                    value={newWsName}
                    onChange={e => setNewWsName(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") void handleCreateWorkspace(); if (e.key === "Escape") { setWsCreateOpen(false); setNewWsName(""); setNewWsIntent(""); } }}
                    placeholder={t("consumerNav.workspaceNamePlaceholder")}
                    className="rounded border border-border bg-bg px-2 py-1 text-[12px] text-text outline-none focus:border-accent"
                  />
                  <input
                    value={newWsIntent}
                    onChange={e => setNewWsIntent(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") void handleCreateWorkspace(); if (e.key === "Escape") { setWsCreateOpen(false); setNewWsName(""); setNewWsIntent(""); } }}
                    placeholder="Goal / intent (optional)…"
                    className="rounded border border-border bg-bg px-2 py-1 text-[12px] text-text outline-none focus:border-accent"
                  />
                  <div className="flex gap-1.5">
                    <button
                      onClick={() => void handleCreateWorkspace()}
                      disabled={!newWsName.trim() || creatingWs}
                      className="flex-1 rounded bg-accent px-2 py-1 text-[11px] font-medium text-white disabled:opacity-40"
                    >{creatingWs ? "Creating…" : "Create"}</button>
                    <button
                      onClick={() => { setWsCreateOpen(false); setNewWsName(""); setNewWsIntent(""); }}
                      className="rounded border border-border px-2 py-1 text-[11px] text-text-muted hover:bg-surface-3"
                    >Cancel</button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setWsCreateOpen(true)}
                  className="flex items-center gap-1.5 rounded-sm py-[6px] px-2 text-[12px] leading-[1.4] transition text-text-muted hover:bg-surface-3 hover:text-text w-full text-left"
                >
                  <span className="text-accent">+</span>
                  <span>New project</span>
                </button>
              )}
              {workspaces.map((ws) => (
                <NavLink
                  key={ws.id}
                  to={`/workspace/${ws.id}`}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    [
                      "block truncate rounded-sm py-[6px] px-2 text-[12px] leading-[1.4] transition select-none",
                      isActive
                        ? "bg-accent-dim font-medium text-text"
                        : "text-text-sec hover:bg-surface-3 hover:text-text",
                    ].join(" ")
                  }
                >
                  {ws.name}
                </NavLink>
              ))}
            </div>
          )}
        </section>

        {/* AI Advisor — expandable with conversation list */}
        <section className="mb-1">
          <button
            onClick={() => setAdvisorOpen((v) => !v)}
            className={[
              "flex w-full items-center gap-2 rounded-sm border-l-2 py-[7px] pr-2 text-[12.5px] leading-[1.4] transition",
              isOnChat && !advisorOpen
                ? "border-accent bg-accent-dim pl-[6px] font-medium text-text"
                : "border-transparent pl-2 text-text-sec hover:bg-surface-3 hover:text-text",
            ].join(" ")}
          >
            <span
              className={`flex h-[13px] w-[13px] shrink-0 items-center justify-center ${
                isOnChat ? "text-accent" : "text-text-muted"
              }`}
            >
              <ChatIcon />
            </span>
            <span className="flex-1 truncate text-left">{t("consumerNav.aiAdvisor")}</span>
            <span
              className={`flex h-[13px] w-[13px] shrink-0 items-center justify-center text-text-muted transition-transform duration-150 ${
                advisorOpen ? "rotate-90" : ""
              }`}
            >
              <ChevronIcon />
            </span>
          </button>

          {advisorOpen && (
            <div className="mt-0.5 ml-[19px] flex flex-col gap-0.5 border-l border-border pl-2 pb-1">
              {/* New conversation — always pinned at top */}
              <NavLink
                to="/chat/new"
                end
                onClick={onNavigate}
                className={({ isActive }) =>
                  [
                    "flex items-center gap-1.5 rounded-sm py-[6px] px-2 text-[12px] leading-[1.4] transition",
                    isActive
                      ? "bg-accent-dim font-medium text-text"
                      : "text-text-muted hover:bg-surface-3 hover:text-text",
                  ].join(" ")
                }
              >
                <span className="text-accent">+</span>
                <span>{t("consumerNav.newConversation")}</span>
              </NavLink>
              {/* Conversation history */}
              {loading ? (
                <div className="py-[6px] px-1 text-[11.5px] text-text-dim">{t("consumerNav.loading")}</div>
              ) : (
                conversations.map((conv) =>
                  renamingId === conv.id ? (
                    <RenameInput
                      key={conv.id}
                      initialValue={renamingInitial}
                      onConfirm={(title) => handleRenameConfirm(conv.id, title)}
                      onCancel={() => setRenamingId(null)}
                    />
                  ) : (
                    <ConvItem
                      key={conv.id}
                      conv={conv}
                      onNavigate={onNavigate}
                      onDelete={handleDelete}
                      onRenameStart={handleRenameStart}
                    />
                  )
                )
              )}
            </div>
          )}
        </section>

        {/* Other nav items */}
        <SidebarNavSection label="" items={OTHER_ITEMS} onNavigate={onNavigate} />

        {/* Suggested prompts */}
        <section>
          <div className="px-1 pb-2 pt-0 text-[9.5px] font-semibold uppercase tracking-[0.1em] text-text-dim">
            {t("consumerNav.askPropwise")}
          </div>
          <div className="flex flex-col gap-0.5">
            {SUGGESTED_PROMPT_KEYS.map((key, i) => (
              <NavLink
                key={i}
                to="/chat/new"
                onClick={onNavigate}
                className="rounded-sm px-2 py-[7px] text-[12.5px] leading-[1.4] text-text-sec italic transition hover:bg-surface-3 hover:text-text"
                style={{ fontFamily: '"Source Serif 4", Georgia, serif' }}
              >
                {t(key)}
              </NavLink>
            ))}
          </div>
        </section>
      </div>

      {/* Profile footer */}
      <ProfileFooter />
    </aside>
  );
}

/* ── Collapsed icon rail (56px wide) ─────────────────────────── */
function CollapsedConsumerSidebar() {
  const location = useLocation();
  const { t } = useTranslation();
  const { user } = useAuthUser();
  const avatarText = user?.name ? initials(user.name) : user?.email ? user.email[0].toUpperCase() : "?";

  const isOn = (prefix: string) =>
    location.pathname === prefix || location.pathname.startsWith(prefix + "/");

  // title holds an i18n key, rendered via t() for the tooltip + aria-label.
  const items: Array<{ to: string; title: string; icon: JSX.Element; active: boolean }> = [
    { to: "/explore",   title: "consumerNav.explore",   icon: <ExploreNavIcon />,   active: isOn("/explore") },
    { to: "/workspace", title: "consumerNav.workspace", icon: <WorkspaceIcon />,    active: isOn("/workspace") },
    { to: "/chat",      title: "consumerNav.aiAdvisor", icon: <ChatIcon />,         active: isOn("/chat") },
    { to: "/market-watch", title: "consumerNav.marketWatch", icon: <WatchIcon />,  active: isOn("/market-watch") },
  ];

  return (
    <aside className="flex h-full w-[56px] min-w-[56px] flex-col items-center gap-1.5 border-r border-border bg-surface py-3">
      {/* Logo glyph */}
      <Link to="/" className="mb-2 flex h-9 w-9 items-center justify-center" aria-label="Home">
        <LogoIcon size={26} />
      </Link>

      {/* Nav icons */}
      <div className="mt-1 flex flex-col gap-1">
        {items.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            title={t(it.title)}
            aria-label={t(it.title)}
            className={[
              "relative flex h-9 w-9 items-center justify-center rounded-md transition",
              it.active
                ? "bg-accent-dim text-accent"
                : "text-text-muted hover:bg-surface-3 hover:text-text",
            ].join(" ")}
          >
            {it.active && (
              <span
                aria-hidden
                className="absolute -left-[10px] top-2 bottom-2 w-[2px] rounded bg-accent"
              />
            )}
            {it.icon}
          </NavLink>
        ))}
      </div>

      <div className="flex-1" />

      {/* Avatar */}
      <Link
        to="/account"
        className="flex h-[30px] w-[30px] items-center justify-center rounded-full bg-accent-dim text-[12px] font-semibold text-accent"
        style={{ fontFamily: '"Source Serif 4", Georgia, serif' }}
        title={user?.name ?? user?.email ?? "Account"}
        aria-label="Account"
      >
        {avatarText}
      </Link>
    </aside>
  );
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function MailIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m2 7 10 6 10-6" />
    </svg>
  );
}

function ProfileFooter() {
  const { user } = useAuthUser();
  const { t } = useTranslation();
  const isPro = user?.role === "pro";
  const displayName = user?.name ?? user?.email ?? "Guest";
  const avatarText = user?.name ? initials(user.name) : "?";
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!menuOpen) return;
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [menuOpen]);

  async function handleSignOut() {
    setMenuOpen(false);
    await signOut();
    navigate("/auth");
  }

  // If signed-out, render a "Sign in" CTA instead of the avatar menu.
  if (!user) {
    return (
      <div className="shrink-0 border-t border-border px-3 py-2.5">
        <Link
          to="/auth"
          className="flex w-full items-center justify-center rounded-md border border-border bg-surface-3 px-3 py-2 text-[12.5px] font-medium text-text-sec transition hover:border-border-strong hover:text-text"
        >
          {t("auth.signIn")}
        </Link>
      </div>
    );
  }

  return (
    <div className="relative shrink-0 border-t border-border px-3 py-2.5" ref={menuRef}>
      {/* Context menu — anchored above the footer */}
      {menuOpen && (
        <div className="absolute left-0 w-48 rounded-md border border-border bg-surface shadow-lg z-50"
          style={{ bottom: "calc(100% + 6px)" }}
        >
          <div className="px-3 py-2 border-b border-border">
            <div className="truncate text-[12px] font-medium text-text">{displayName}</div>
            <div className="text-[10.5px] text-text-muted truncate">{user?.email}</div>
          </div>
          <div className="p-1">
            <Link
              to="/account"
              onClick={() => setMenuOpen(false)}
              className="flex w-full items-center gap-2 rounded px-2 py-[7px] text-[12.5px] text-text-sec transition hover:bg-surface-3 hover:text-text"
            >
              <CogIcon />
              {t("auth.account.title")}
            </Link>
            <Link
              to="/contact"
              onClick={() => setMenuOpen(false)}
              className="flex w-full items-center gap-2 rounded px-2 py-[7px] text-[12.5px] text-text-sec transition hover:bg-surface-3 hover:text-text"
            >
              <MailIcon />
              {t("contact.title")}
            </Link>
            <button
              onClick={() => void handleSignOut()}
              className="flex w-full items-center gap-2 rounded px-2 py-[7px] text-[12.5px] text-text-sec transition hover:bg-surface-3 hover:text-text"
            >
              <SignOutIcon />
              {t("auth.signOut")}
            </button>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2">
        <button
          onClick={() => setMenuOpen((v) => !v)}
          className={[
            "flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full text-[12px] font-semibold transition",
            menuOpen ? "ring-2 ring-accent ring-offset-1 ring-offset-surface" : "hover:ring-2 hover:ring-border hover:ring-offset-1 hover:ring-offset-surface",
          ].join(" ")}
          style={{
            fontFamily: '"Source Serif 4", Georgia, serif',
            background: "var(--accent-dim)",
            color: "var(--accent)",
          }}
          aria-label="Account menu"
        >
          {avatarText}
        </button>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[12px] font-medium text-text">{displayName}</div>
          <div className="text-[10.5px] text-text-muted">{isPro ? t("consumerNav.proPlan") : t("consumerNav.freePlan")}</div>
        </div>
        {!isPro && (
          <button className="shrink-0 rounded border border-border px-2 py-1 text-[10.5px] text-text-muted transition hover:border-border-strong hover:text-text-sec">
            {t("consumerNav.upgrade")}
          </button>
        )}
      </div>
    </div>
  );
}

const SIDEBAR_COLLAPSED_KEY = STORAGE_UI.consumerSidebarCollapsed;

export function AppShellConsumer() {
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1";
    } catch {
      return false;
    }
  });
  const location = useLocation();
  const { t } = useTranslation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? "1" : "0");
    } catch {
      /* ignore quota / privacy-mode errors */
    }
  }, [collapsed]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    document.body.style.overscrollBehavior = "none";
    document.body.style.position = "fixed";
    document.body.style.width = "100%";
    document.body.style.top = "0";
    document.body.style.left = "0";
    return () => {
      document.body.style.overflow = "";
      document.body.style.overscrollBehavior = "";
      document.body.style.position = "";
      document.body.style.width = "";
      document.body.style.top = "";
      document.body.style.left = "";
    };
  }, []);

  return (
    <div className="theme-consumer flex h-[100dvh] w-full overflow-hidden bg-bg text-text">
      {/* Desktop sidebar — crossfades between full panel (264px) and icon rail (56px) */}
      <div
        className="relative hidden h-full md:block"
        style={{
          flexShrink: 0,
          width: collapsed ? 56 : 264,
          transition: "width 320ms cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {/* Expanded — sits underneath while collapsed */}
        <div
          className="absolute inset-y-0 left-0 overflow-hidden"
          style={{
            width: 264,
            opacity: collapsed ? 0 : 1,
            pointerEvents: collapsed ? "none" : "auto",
            transition: collapsed
              ? "opacity 140ms ease-out"
              : "opacity 220ms ease-in 100ms",
          }}
          aria-hidden={collapsed}
        >
          <ConsumerSidebar />
        </div>

        {/* Collapsed icon rail */}
        <div
          className="absolute inset-y-0 left-0 overflow-hidden"
          style={{
            width: 56,
            opacity: collapsed ? 1 : 0,
            pointerEvents: collapsed ? "auto" : "none",
            transition: collapsed
              ? "opacity 220ms ease-in 100ms"
              : "opacity 140ms ease-out",
          }}
          aria-hidden={!collapsed}
        >
          <CollapsedConsumerSidebar />
        </div>

        {/* Edge pill toggle */}
        <button
          onClick={() => setCollapsed((v) => !v)}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className="group absolute z-10 flex h-[22px] w-[22px] items-center justify-center rounded-full border bg-surface p-0 text-text-muted shadow-sm transition hover:border-accent hover:bg-accent hover:text-bg"
          style={{
            top: 52,
            right: -11,
            borderColor: "var(--color-border-strong, rgba(23,32,29,0.18))",
          }}
        >
          <svg
            width="10"
            height="10"
            viewBox="0 0 14 14"
            fill="none"
            style={{
              transform: collapsed ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 320ms cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            <path
              d="M9 3l-4 4 4 4"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {/* Mobile drawer */}
      {open && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm md:hidden"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          <div className="fixed inset-y-0 left-0 z-50 w-[78%] max-w-[320px] md:hidden animate-slide-in-left">
            <ConsumerSidebar onNavigate={() => setOpen(false)} />
          </div>
        </>
      )}

      <div className="relative flex min-w-0 flex-1 flex-col min-h-0 overflow-hidden">
        {/* Mobile top bar */}
        <header className="flex items-center gap-3 border-b border-border-subtle bg-surface/80 backdrop-blur px-3 py-2 md:hidden">
          <button
            aria-label={t("nav.menu")}
            className="rounded-md p-1.5 text-text-sec hover:bg-surface-3 hover:text-text"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <CloseIcon size={20} /> : <MenuIcon size={20} />}
          </button>
          <Link to="/" className="flex items-center gap-2">
            <LogoIcon size={22} />
            <span
              className="text-[15px] font-semibold tracking-[-0.02em] text-text"
              style={{ fontFamily: '"Source Serif 4", Georgia, serif' }}
            >
              ProProp<span className="text-accent">.</span>
            </span>
          </Link>
        </header>

        <EmailVerificationBanner />

        <main className="flex-1 overflow-hidden min-h-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/* ── Nav icons ───────────────────────────────────────────────── */
function ExploreNavIcon() {
  return (
    <svg width={13} height={13} viewBox="0 0 14 14" fill="none">
      <circle cx="7" cy="7" r="5.3" stroke="currentColor" strokeWidth="1.3" />
      <path d="M9.5 4.5L8 8l-3.5 1.5L6 6z" fill="currentColor" opacity="0.85" />
    </svg>
  );
}
function WorkspaceIcon() {
  return (
    <svg width={13} height={13} viewBox="0 0 14 14" fill="none">
      <rect x="1.5" y="4" width="11" height="8" rx="1" stroke="currentColor" strokeWidth="1.3" />
      <path d="M4.5 4V3a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v1" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      <path d="M1.5 8h11" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}
function ChatIcon() {
  return (
    <svg width={13} height={13} viewBox="0 0 14 14" fill="none">
      <path d="M2 2.5h10a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-.5.5H8l-2 2-2-2H2a.5.5 0 0 1-.5-.5V3a.5.5 0 0 1 .5-.5z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  );
}
function WatchIcon() {
  return (
    <svg width={13} height={13} viewBox="0 0 14 14" fill="none">
      <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.3" />
      <path d="M7 4.5V7l1.5 1.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}
function ChevronIcon() {
  return (
    <svg width={10} height={10} viewBox="0 0 10 10" fill="none">
      <path d="M3.5 2L6.5 5L3.5 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function CogIcon() {
  return (
    <svg width={13} height={13} viewBox="0 0 14 14" fill="none">
      <circle cx="7" cy="7" r="2" stroke="currentColor" strokeWidth="1.3" />
      <path d="M7 1.5v1.5M7 11v1.5M11.5 7H13M1 7h1.5M10.2 3.8l1-1M2.8 11.2l1-1M10.2 10.2l1 1M2.8 2.8l1 1" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}
function SignOutIcon() {
  return (
    <svg width={13} height={13} viewBox="0 0 14 14" fill="none">
      <path d="M5.5 2H3a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h2.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M9.5 9.5L12 7l-2.5-2.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12 7H5.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}
function PencilIcon() {
  return (
    <svg width={13} height={13} viewBox="0 0 14 14" fill="none">
      <path d="M9.5 2.5l2 2L4 12H2v-2L9.5 2.5z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  );
}
function TrashIcon() {
  return (
    <svg width={13} height={13} viewBox="0 0 14 14" fill="none">
      <path d="M2.5 4h9M5.5 4V2.5h3V4M6 6.5v4M8 6.5v4M3.5 4l.5 8h6l.5-8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
